import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const url = e.target.value;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
      e.target.value = "";
    }
  };

  const navLinks = [
    { to: "/", label: t("nav.home"), icon: "ri-home-line" },
    { to: "/artykul/kakobuy-spreadsheet-poradnik", label: t("nav.shoppingGuide"), icon: "ri-shopping-bag-3-line" },
  ];

  const articleLinks = [
    { to: "/artykul/kakobuy-spreadsheet-poradnik", label: "Poradnik Kakobuy Spreadsheet", icon: "ri-guide-line" },
    { to: "/artykul/kakobuy-bezpieczenstwo", label: "Bezpieczeństwo Kakobuy", icon: "ri-shield-check-line" },
    { to: "/artykul/kakobuy-wysylka-polska", label: "Wysyłka do Polski", icon: "ri-ship-line" },
    { to: "/artykul/kakobuy-porownanie-agentow", label: "Porównanie Agentów", icon: "ri-scales-3-line" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          <Link
            to="/"
            className="flex items-center gap-2 group flex-shrink-0"
          >
            <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-primary-500 rounded-lg">
              <span className="text-background-50 text-lg md:text-xl font-heading font-bold leading-none">K</span>
            </div>
            <span className="text-foreground-900 font-heading text-lg md:text-xl font-semibold whitespace-nowrap">
              Kakobuy <span className="text-primary-500">Polska</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className="flex items-center gap-1.5 text-foreground-700 hover:text-primary-500 font-medium text-sm transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line text-sm"></i>
              {t("nav.home")}
            </Link>

            <Link
              to="/artykul/kakobuy-spreadsheet-poradnik"
              className="flex items-center gap-1.5 text-foreground-700 hover:text-primary-500 font-medium text-sm transition-colors whitespace-nowrap"
            >
              <i className="ri-shopping-bag-3-line text-sm"></i>
              {t("nav.shoppingGuide")}
            </Link>

            <div className="relative">
              <select
                onChange={handleDropdownChange}
                defaultValue=""
                className="appearance-none bg-transparent text-foreground-700 hover:text-primary-500 font-medium text-sm transition-colors cursor-pointer pr-5 focus:outline-none whitespace-nowrap"
              >
                <option value="" disabled>
                  {t("nav.categories")}
                </option>
                {categories.map((cat) => (
                  <option key={cat.name} value={cat.url}>
                    {cat.label}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-0 top-1/2 -translate-y-1/2 text-foreground-600 text-xs pointer-events-none"></i>
            </div>

            <Link
              to="/artykul/kakobuy-bezpieczenstwo"
              className="text-foreground-700 hover:text-primary-500 font-medium text-sm transition-colors whitespace-nowrap"
            >
              {t("nav.safety")}
            </Link>

            <Link
              to="/artykul/kakobuy-wysylka-polska"
              className="text-foreground-700 hover:text-primary-500 font-medium text-sm transition-colors whitespace-nowrap"
            >
              {t("nav.shipping")}
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("button.kakobuy")}
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900 cursor-pointer"
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 py-4 px-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
            {/* Main Navigation */}
            <div className="pb-3 mb-3 border-b border-background-200/70">
              <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2 px-1">Nawigacja</p>
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2.5 text-foreground-700 font-medium text-sm py-2.5 px-1 hover:text-primary-500 transition-colors whitespace-nowrap rounded-lg hover:bg-primary-50/50"
                >
                  <i className={`${link.icon} text-base w-5 flex items-center justify-center`}></i>
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Categories */}
            <div className="pb-3 mb-3 border-b border-background-200/70">
              <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2 px-1">Kategorie produktów</p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 text-foreground-600 text-xs py-2 px-1.5 hover:text-primary-500 transition-colors whitespace-nowrap rounded-lg hover:bg-primary-50/50"
                  >
                    <i className={`${cat.icon} text-sm w-4 flex items-center justify-center`}></i>
                    {cat.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Articles */}
            <div className="pb-3 mb-3 border-b border-background-200/70">
              <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2 px-1">Poradniki i artykuły</p>
              {articleLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2.5 text-foreground-600 text-sm py-2.5 px-1 hover:text-primary-500 transition-colors whitespace-nowrap rounded-lg hover:bg-primary-50/50"
                >
                  <i className={`${link.icon} text-sm w-5 flex items-center justify-center`}></i>
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer mt-1"
            >
              {t("button.kakobuy")}
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>
        )}
      </nav>
    </header>
  );
}